import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, MailIcon, SendIcon, CheckCircle2Icon } from 'lucide-react';
export function SequenceHero() {
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 2500);
    return () => clearInterval(interval);
  }, []);
  const sequenceSteps = [{
    day: 'Day 1',
    type: 'Initial email',
    subject: 'Quick question about {{company}}'
  }, {
    day: 'Day 4',
    type: 'Follow-up',
    subject: 'Re: Quick question about {{company}}'
  }, {
    day: 'Day 7',
    type: 'Break-up',
    subject: 'Checking in one last time'
  }];
  return <section className="relative pt-16 pb-24 overflow-hidden bg-white" data-id="element-1526">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1527">
        <a href="#/features" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-1528">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1529" /> All features
        </a>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1530">
          {/* Left Column: Copy & CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-1531">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.05]" data-id="element-1532">
              Turn local leads into <br data-id="element-1533" />
              <span className="text-blue-600" data-id="element-1534">booked meetings</span>
            </h1>

            <p className="text-xl sm:text-2xl text-slate-600 mb-8 max-w-lg leading-relaxed" data-id="element-1535">
              The best email sequence software for sales teams. Combine{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-blue-50 text-blue-700 text-lg sm:text-xl font-medium mx-1" data-id="element-1536">
                verified data
              </span>{' '}
              with{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-green-50 text-green-700 text-lg sm:text-xl font-medium mx-1 mt-2 sm:mt-0" data-id="element-1537">
                automated outreach
              </span>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-1538">
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-10 py-4 text-white font-bold bg-blue-600 rounded-full hover:bg-blue-700 transition-colors text-lg text-center flex items-center justify-center gap-2" data-id="element-1539">
                Try for free <ArrowRightIcon className="w-5 h-5" data-id="element-1540" />
              </a>
            </div>

            {/* Static Stats Bar */}
            <div className="flex gap-12 pt-8" data-id="element-1541">
              <div data-id="element-1542">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-1543">
                  50%+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-1544">
                  Avg. open rates
                </div>
              </div>
              <div data-id="element-1545">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-1546">
                  10x
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-1547">
                  Faster prospecting
                </div>
              </div>
              <div data-id="element-1548">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-1549">
                  Native
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-1550">
                  CRM sequences
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Live Data Preview Card */}
          <div className="relative" data-id="element-1551">
            <div className="bg-slate-50 rounded-[2rem] p-8 sm:p-10 relative z-10 border border-slate-100 shadow-xl" data-id="element-1552">
              <div className="flex justify-between items-center mb-8" data-id="element-1553">
                <div data-id="element-1554">
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-1555">
                    B2B email sequence
                  </h3>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-1556">
                    Campaign: Local Restaurants Q3
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1557">
                  <SendIcon className="w-6 h-6 text-blue-600 ml-1" data-id="element-1558" />
                </div>
              </div>

              <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-1559">
                {sequenceSteps.map((step, idx) => <div key={idx} className={`relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active transition-all duration-500 ${activeStep === idx ? 'opacity-100 scale-105' : 'opacity-50 scale-100'}`} data-id="element-1560">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-50 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1561">
                      <MailIcon className="w-4 h-4" data-id="element-1562" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl shadow-sm border border-slate-100" data-id="element-1563">
                      <div className="flex items-center justify-between mb-2" data-id="element-1564">
                        <span className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-1565">
                          {step.day}
                        </span>
                        <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-1566">
                          {step.type}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 truncate" data-id="element-1567">
                        {step.subject}
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200/60 flex justify-between items-center" data-id="element-1568">
                <div className="flex items-center gap-2 text-sm font-medium text-green-600 bg-green-50 px-3 py-1.5 rounded-full" data-id="element-1569">
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-1570" /> 245 prospects
                  enrolled
                </div>
                <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1571">
                  Launch sequence
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}